import { lazy, type ComponentType } from "react";
import { attemptChunkReload, reloadSuppressed } from "./reloadGuard";

/** Resolve once the tab is foreground AND online again (or after a safety
 *  timeout), so a retry runs when the import can actually succeed. */
function waitForResumable(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  const ready = () =>
    document.visibilityState === "visible" &&
    (typeof navigator === "undefined" || navigator.onLine !== false);
  if (ready()) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const done = () => {
      if (!ready()) return;
      cleanup();
      resolve();
    };
    const cleanup = () => {
      document.removeEventListener("visibilitychange", done);
      window.removeEventListener("online", done);
      clearTimeout(safety);
    };
    const safety = setTimeout(() => {
      cleanup();
      resolve();
    }, 30_000);
    document.addEventListener("visibilitychange", done);
    window.addEventListener("online", done);
  });
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Retry wrapper for lazy imports · handles stale chunk hashes after deploys.
 *
 * On factory failure (or a stale chunk that resolves with no default export):
 * reload once to refetch index.html + the new hashed chunks, via the shared
 * time-based guard (attemptChunkReload). The guard recovers from EVERY later
 * deploy in the same session, but blocks a reload loop — if a chunk re-fails
 * within the cooldown the reload clearly didn't help, so we rethrow and let the
 * error boundary handle it. (Replaces the old per-module one-shot key, which
 * recovered the first stale chunk but crashed the second one.)
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  name?: string,
): React.LazyExoticComponent<T> {
  return lazy(() => {
    return factory()
      .then((mod) => {
        // Stale-chunk detection (Sentry CR-DASHBOARD-29, 2026-05-27):
        // CDN occasionally serves a chunk file from an older build that
        // doesn't contain the named export the current entry expects. The
        // import() resolves successfully but `mod.default` is undefined,
        // so React.lazy renders `undefined` as a component → TypeError. The
        // factory's .catch never fires because nothing threw. Promote it to
        // a thrown error here so the same reload-once recovery kicks in.
        if (!mod || (mod as { default?: unknown }).default === undefined) {
          throw new Error(
            `lazyRetry: ${name || "module"} loaded with no default export — stale chunk after deploy`,
          );
        }
        return mod;
      })
      .catch(async (err) => {
        // A held (scan in flight), backgrounded, or offline tab routinely drops
        // an in-flight dynamic import. Reloading there would discard the user's
        // work, so instead wait for the tab to come back to the foreground+online
        // and retry the import — the drop is transient and the retry almost
        // always succeeds. This is what previously broke an in-progress scan when
        // the user switched tabs: lazyRetry reloaded right past the scan's hold.
        if (reloadSuppressed()) {
          for (let i = 0; i < 3; i++) {
            await waitForResumable();
            await sleep(300 + i * 400);
            try {
              const mod = await factory();
              if (mod && (mod as { default?: unknown }).default !== undefined) {
                return mod as { default: T };
              }
            } catch {
              /* keep retrying */
            }
          }
          // Still failing after the tab returned: fall through to the stale-chunk
          // path (now unsuppressed, so a genuine post-deploy chunk can recover).
        }
        if (attemptChunkReload()) return new Promise<{ default: T }>(() => {});
        throw err;
      });
  });
}
